// Small runtime fix: disambiguate generic CTA links by appending a readable
// destination to their accessible name. The visible text is always kept as a
// prefix so the accessible name still contains it (WCAG 2.5.3, Label in Name).
import { onDocumentReady } from '@theme/utilities';

/**
 * Turns a link's href into words a screen reader can announce, e.g.
 * "/collections/cpap-masks?filter.p.vendor=ResMed" → "cpap masks".
 * Returns null when the destination would add nothing useful (no href,
 * same-page anchors, non-web schemes, or the store's home page).
 * @param {string | null} href
 * @returns {string | null}
 */
export function humanizeDestination(href) {
  const raw = (href || '').trim();
  if (!raw || raw.startsWith('#')) return null;

  let url;
  try {
    url = new URL(raw, document.baseURI);
  } catch {
    return null;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;

  const isExternal = url.host !== window.location.host;
  const hostname = url.hostname.replace(/^www\./, '');
  const segments = url.pathname
    .split('/')
    .filter(Boolean)
    .map((segment) => {
      try {
        return decodeURIComponent(segment);
      } catch {
        return segment;
      }
    });

  if (segments.length === 0) return isExternal ? hostname : null;

  const lastSegment = segments[segments.length - 1];
  const isAllProducts = lastSegment === 'all' && segments[segments.length - 2] === 'collections';
  const words = isAllProducts
    ? 'all products'
    : lastSegment
        .replace(/\.[a-z0-9]{1,5}$/i, '')
        .replace(/[-_+]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

  if (!words) return null;
  return isExternal ? `${words} (${hostname})` : words;
}

onDocumentReady(() => {
  const generic = new Set([
    'shop now',
    'shop',
    'learn more',
    'read more',
    'click here',
    'buy now',
    'shop now ›',
    'shop now »'
  ]);

  const candidates = Array.from(document.querySelectorAll('a, [role="link"]'));
  candidates.forEach((el) => {
    if (el.hasAttribute('aria-label') || el.hasAttribute('aria-labelledby')) return;
    // Use textContent to avoid forcing layout (faster than innerText)
    const text = (el.textContent || '').trim();
    if (!text) return;
    const key = text.toLowerCase();
    if (!generic.has(key)) return;

    const destination = humanizeDestination(el.getAttribute('href'));
    if (!destination) return;
    el.setAttribute('aria-label', `${text} — ${destination}`);
  });
});
